import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';
import { useAuth } from '../context/AuthContext';
import FileUpload from '../components/FileUpload';
import ManualTradeEntry from '../components/ManualTradeEntry';
import TradingPerformance from '../components/TradingPerformance';
import PnLSummary from '../components/PnLSummary';
import NotesPnLLeaderboard from '../components/NotesPnLLeaderboard';
import UserManagement from '../components/UserManagement';
import FeedbackForm from '../components/FeedbackForm';
import FeedbackQueue from '../components/FeedbackQueue';
import './Dashboard.css';

function Dashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('tradebooks'); // Default to Trade Books
  const [activeSubTab, setActiveSubTab] = useState('performance'); // Default sub-tab for Trade Books
  const [activeUploadSubTab, setActiveUploadSubTab] = useState('manual'); // manual | file
  const [tradesData, setTradesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tradeBooks, setTradeBooks] = useState([]);
  const [selectedTradeBookId, setSelectedTradeBookId] = useState(null);
  const [loadingTradeBooks, setLoadingTradeBooks] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [connectedBrokers, setConnectedBrokers] = useState([]);
  const [expiredBrokers, setExpiredBrokers] = useState([]);

  useEffect(() => {
    fetchTradeBooks();
    fetchBrokerStatus();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    if (p.get('tab') === 'upload') {
      setActiveTab('upload');
      setActiveUploadSubTab('manual');
    }
  }, []);

  const fetchBrokerStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/brokers/status`);
      const all = res.data.brokers || [];
      setConnectedBrokers(all.filter((b) => b.connected).map((b) => ({ broker: b.id, label: b.label })));
      setExpiredBrokers(all.filter((b) => b.sessionExpired).map((b) => b.label));
    } catch (e) {
      setConnectedBrokers([]);
      setExpiredBrokers([]);
    }
  };

  useEffect(() => {
    if (!feedbackModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setFeedbackModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [feedbackModalOpen]);

  useEffect(() => {
    if (selectedTradeBookId) {
      fetchTrades(selectedTradeBookId);
    }
  }, [selectedTradeBookId]);

  const fetchTradeBooks = async () => {
    try {
      setLoadingTradeBooks(true);
      const response = await axios.get(`${API_BASE_URL}/api/trade-books`);
      setTradeBooks(response.data);
      // Set the latest trade book as selected by default
      if (response.data.length > 0) {
        setSelectedTradeBookId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching trade books:', error);
      setTradeBooks([]);
    } finally {
      setLoadingTradeBooks(false);
    }
  };

  const fetchTrades = async (tradeBookId = null) => {
    try {
      setLoading(true);
      const url = tradeBookId 
        ? `${API_BASE_URL}/api/trades?tradeBookId=${tradeBookId}`
        : `${API_BASE_URL}/api/trades`;
      const response = await axios.get(url);
      console.log('Trades data received:', response.data);
      setTradesData(response.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error('Authentication error - token may be invalid');
      }
      setTradesData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRetrieve = () => {
    if (selectedTradeBookId) {
      fetchTrades(selectedTradeBookId);
    }
  };

  const handleDeleteTradeBook = async () => {
    if (!selectedTradeBookId) return;
    
    const selectedTradeBook = tradeBooks.find(tb => tb.id === selectedTradeBookId);
    const tradeBookName = selectedTradeBook ? selectedTradeBook.name : 'this trade book';
    
    if (!window.confirm(`Are you sure you want to delete "${tradeBookName}"? This will permanently delete the trade book and all its trades. This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/trade-books/${selectedTradeBookId}`);
      
      // Refresh trade books list
      await fetchTradeBooks();
      
      // The fetchTradeBooks will automatically select the latest trade book
      // and the useEffect will fetch trades for it
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete trade book');
    }
  };

  const handleFileUpload = async () => {
    await fetchTradeBooks();
    await fetchBrokerStatus();
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-brand">
            <img src="/tradelensy-logo.png" alt="Tradelensy logo" className="header-logo" />
            <h1>Tradelensy</h1>
          </div>
          <div className="header-actions">
            <div className="header-connected-brokers" aria-label="Connected brokers">
              <span className="header-connected-brokers__label">Connected Brokers:</span>
              {connectedBrokers.length === 0 ? (
                <span className="header-connected-brokers__none">None</span>
              ) : (
                connectedBrokers.map((b) => (
                  <span key={b.broker} className="header-connected-brokers__chip">
                    {b.label}
                  </span>
                ))
              )}
              {expiredBrokers.length > 0 && (
                <span className="header-connected-brokers__expired" title="Reconnect from Add/Edit Trades → Trade Wizard">
                  {expiredBrokers.join(', ')} session expired
                </span>
              )}
            </div>
            <span className="user-info">
              {user?.name} ({user?.role})
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-header-action"
              onClick={() => setFeedbackModalOpen(true)}
            >
              Feedback
            </button>
            <button type="button" onClick={handleLogout} className="btn btn-secondary btn-header-action">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <nav className="top-tabs">
          <button
            className={`top-tab ${activeTab === 'tradebooks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tradebooks')}
          >
            Trade Books
          </button>
          {user?.role === 'admin' && (
            <button
              className={`top-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              User Management
            </button>
          )}
          {user?.role === 'admin' && (
            <button
              className={`top-tab ${activeTab === 'feedbackQueue' ? 'active' : ''}`}
              onClick={() => setActiveTab('feedbackQueue')}
            >
              Feedback queue
            </button>
          )}
          <button
            className={`top-tab ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            Add/Edit Trades
          </button>
        </nav>

        <div className="main-content">
          {activeTab === 'feedbackQueue' && user?.role === 'admin' && <FeedbackQueue />}
          {activeTab === 'upload' && (
            <div className="upload-tabs-container">
              <nav className="upload-sub-tabs">
                <button
                  className={`upload-sub-tab ${activeUploadSubTab === 'manual' ? 'active' : ''}`}
                  onClick={() => setActiveUploadSubTab('manual')}
                >
                  ✨ Trade Wizard ✨
                </button>
                <button
                  className={`upload-sub-tab ${activeUploadSubTab === 'file' ? 'active' : ''}`}
                  onClick={() => setActiveUploadSubTab('file')}
                >
                  Upload File
                </button>
              </nav>

              {activeUploadSubTab === 'manual' && (
                <ManualTradeEntry onSuccess={handleFileUpload} />
              )}
              {activeUploadSubTab === 'file' && (
                <FileUpload onUploadSuccess={handleFileUpload} />
              )}
            </div>
          )}
          {activeTab === 'tradebooks' && (
            <div className="trade-books-container">
              <div className="trade-book-selector card">
                <div className="selector-row">
                  <div className="form-group" style={{ flex: 1, marginRight: '15px' }}>
                    <label htmlFor="trade-book-select">Select Trade Book:</label>
                    <select
                      id="trade-book-select"
                      value={selectedTradeBookId || ''}
                      onChange={(e) => setSelectedTradeBookId(e.target.value)}
                      className="trade-book-select"
                      disabled={loadingTradeBooks || tradeBooks.length === 0}
                    >
                      {loadingTradeBooks ? (
                        <option>Loading trade books...</option>
                      ) : tradeBooks.length === 0 ? (
                        <option>No trade books available</option>
                      ) : (
                        tradeBooks.map((tb) => (
                          <option key={tb.id} value={tb.id}>
                            {tb.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleRetrieve}
                    disabled={!selectedTradeBookId || loading}
                    style={{ marginTop: '24px' }}
                  >
                    Retrieve
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteTradeBook}
                    disabled={!selectedTradeBookId || loadingTradeBooks || tradeBooks.length === 0}
                    style={{ marginTop: '24px' }}
                  >
                    Delete this Trade Book
                  </button>
                </div>
              </div>

              <nav className="sub-tabs">
                <button
                  className={`sub-tab ${activeSubTab === 'performance' ? 'active' : ''}`}
                  onClick={() => setActiveSubTab('performance')}
                >
                  Trading Performance
                </button>
                <button
                  className={`sub-tab ${activeSubTab === 'pnl' ? 'active' : ''}`}
                  onClick={() => setActiveSubTab('pnl')}
                >
                  P&L Summary
                </button>
                <button
                  className={`sub-tab ${activeSubTab === 'notesPnl' ? 'active' : ''}`}
                  onClick={() => setActiveSubTab('notesPnl')}
                >
                  Journal Stats
                </button>
              </nav>

              {activeSubTab === 'performance' && (
                <>
                  {loading && <div className="card">Loading trade data...</div>}
                  <TradingPerformance tradesData={tradesData} />
                </>
              )}
              {activeSubTab === 'pnl' && (
                <>
                  {loading && <div className="card">Loading trade data...</div>}
                  <PnLSummary tradesData={tradesData} />
                </>
              )}
              {activeSubTab === 'notesPnl' && (
                <NotesPnLLeaderboard tradeBookId={selectedTradeBookId} />
              )}
            </div>
          )}
          {activeTab === 'users' && user?.role === 'admin' && (
            <UserManagement />
          )}
        </div>
      </div>

      {feedbackModalOpen && (
        <div
          className="feedback-modal-backdrop"
          role="presentation"
          onClick={() => setFeedbackModalOpen(false)}
        >
          <div
            className="feedback-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="feedback-modal-dismiss"
              aria-label="Close feedback form"
              onClick={() => setFeedbackModalOpen(false)}
            >
              ×
            </button>
            <FeedbackForm isOpen={feedbackModalOpen} onClose={() => setFeedbackModalOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
